<template>
  <div>
    <form
      v-if="isGvntVisible"
      @submit.prevent="updateGvntWithConfirmation"
    >

      <div class="mb-4">
        <h5>Finish</h5>

        <b-form-datepicker
          v-model="plannedFinishDateStr"
          :min="minPlannedFinishDateStr"
          :start-weekday="MONDAY"
          :readonly="isFtimeReadonly"
          class="mb-2"
        />

        <time-input
          v-model="plannedFinishTimeStr"
          :readonly="isFtimeReadonly"
        />
      </div>

      <hr>

      <b-button :to="{name: 'gvnt_info'}">Cancel</b-button>

      <b-button
        type="submit"
        :disabled="isReadonly"
        variant="primary"
      >
        Save
      </b-button>
    </form>

    <loading-indicator v-if="isLoading"/>
  </div>
</template>

<script>
import notifier from '../notifier.js';
import { HTTP } from '../http-common.js';

import { isGvntEditable      } from '../model/gvnt.js';
import { isGvntFtimeEditable } from '../model/gvnt.js';

import { confirmAction } from '../service/confirm.js';

import { convertStampToDateStr               } from '../util.js';
import { convertStampToTimeStr               } from '../util.js';
import { convertMomentToDateStr              } from '../util.js';
import { convertDataStrAndTimeStrToLocalData } from '../util.js';

import LoadingIndicator from './LoadingIndicator.vue';
import TimeInput        from './TimeInput.vue';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'GvntEdit',

  components: {
    LoadingIndicator,
    TimeInput,
  },

  props: {
    gvntId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      MONDAY: 1,

      loadingStatus: LOADING_STATUS__INIT,
      gvnt: null,

      plannedFinishDateStr: '',
      minPlannedFinishDateStr: '',
      plannedFinishTimeStr: '',
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {boolean} */
    isGvntVisible() {
      if(!this.isLoadedNormal) {
        return false;
      }

      return this.gvnt !== null;
    },

    /** @returns {boolean} */
    isReadonly() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return true;
      }

      const isEditable = isGvntEditable(gvnt.status);
      return !isEditable;
    },

    /** @returns {boolean} */
    isFtimeEditable() {
      const gvnt = this.gvnt;
      if(gvnt === null) {
        return false;
      }

      return isGvntFtimeEditable(gvnt.status);
    },

    /** @returns {boolean} */
    isFtimeReadonly() {
      return !this.isFtimeEditable;
    },

    /** @returns {?Date} */
    plannedFinishMoment() {
      return convertDataStrAndTimeStrToLocalData(this.plannedFinishDateStr, this.plannedFinishTimeStr);
    },
  },

  created() {
    this.loadGvnt();
  },

  methods: {
    loadGvnt() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const url = `/gvnt/${this.gvntId}/info`;
      HTTP.get(url)
      .then(response => {
        const gvnt = response.data.data;
        this.gvnt = gvnt;

        const now = new Date();
        const nowDateStr = convertMomentToDateStr(now);

        this.plannedFinishDateStr    = convertStampToDateStr(gvnt.ftime);
        this.minPlannedFinishDateStr = nowDateStr;
        this.plannedFinishTimeStr    = convertStampToTimeStr(gvnt.ftime);

        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    updateGvntWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      this.confirmGvntUpdate(() => {
        this.updateGvnt();
      });
    },

    // function onConfirmed()
    confirmGvntUpdate(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to save changes for this game event {env}?',
        'Save',
        onConfirmed,
      );
    },

    updateGvnt() {
      const plannedFinishSec = Math.floor(this.plannedFinishMoment.getTime() / 1000);

      const data = new FormData();
      data.set('ftime', plannedFinishSec);

      const url = `/gvnt/${this.gvntId}`;
      HTTP.post(url, data)
      .then(() => {
        notifier.showSuccessAfterTransition('Changes saved.');
        this.$router.push({name: 'gvnt_info', params: {gvntId: this.gvntId}});
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    }
  },
}
</script>
